/* eslint-disable object-shorthand */
import type { FC, ComponentType } from 'react';
import React, { forwardRef, useEffect, useCallback, useRef, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { css } from '@compiled/react';

import CrossIcon from '@atlaskit/icon/core/cross';
import Button from '@atlaskit/button/button';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import EmojiIcon from '@atlaskit/icon/core/emoji';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import type { PopupComponentProps } from '@atlaskit/popup/types';
import Spinner from '@atlaskit/spinner/spinner';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { withSessionData } from '@confluence/session-data/entry-points/withSessionData.ts';
import type { SessionDataContextInjected } from '@confluence/session-data/entry-points/withSessionData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { NTH_USER_EMOJI_TITLE_DISCOVERED } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';

import { useAIEmojiSuggestion } from './AIEmojiSuggestionStore.ts';
import type { EmojiTitleComponentPopoverProps } from './__types__/EmojiTitle.ts';
import { ConfluenceGraphQLDefaultTitleEmoji as DefaultTitleEmojiValues } from './__types__/EmojiTitleButtonUpdateDefaultTitleEmojiMutation.ts';
import { DefaultTitleEmojiValues as DefaultTitleEmojiContentPropertyValues } from '../transformer.ts';

const i18n = defineMessages({
	removeEmoji: {
		id: 'emoji-title.remove-emoji',
		description:
			'Text in the button to prompt user to remove emoji from editor title when they hover over the title in editor.',
		defaultMessage: 'Remove emoji',
	},
	emojiLabel: {
		id: 'emoji-title.add-emoji.floating-toolbar',
		description:
			'Text in the button to prompt user to add emoji to editor title when they hover over the title in editor and button is located on the floating toolbar.',
		defaultMessage: 'Emoji',
	},
	restoreEmoji: {
		id: 'emoji-title.restore-emoji',
		description:
			'Text in the button to prompt user to restore emoji to editor title if emoji has been removed.',
		defaultMessage: 'Restore emoji',
	},
});

const emojiIconStyles = css({
	pointerEvents: 'none',
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => {
	const { style, ...otherProps } = props;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
	return <div {...otherProps} ref={ref} style={{ ...style }} />;
});

const EmojiTitlePopoverComponent: FC<
	EmojiTitleComponentPopoverProps & SessionDataContextInjected
> = ({
	contentId,
	hasEmoji,
	onRemoveEmoji,
	isLoading,
	emojiPicker,
	setOpenSecondEmojiPicker,
	setIsEmojiButtonFocused,
	openSecondEmojiPicker,
	onEmojiPickerLoaded,
	userOffline,
	floatingToolbarButtonStyle,
	updateDefaultEmoji,
	hasLiveDocsDefaultEmoji,
	autoPageEmojiCohort,
	emojiTitleDiscovered,
	contentType,
	previousEmojiId,
	restorePreviousEmoji,
	isSmartCreate,
	currentEmojiId,
	emojiTitleState,
}) => {
	const [
		{
			aifcSuggestionInFlight,
			aiSuggestedEmojiId,
			aiEmojiSuggestionSource,
			aiEmojiSuggestionSessionId,
		},
	] = useAIEmojiSuggestion();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isLivePage = useIsCurrentPageLive();
	const { setOnboardingState } = useSetOnboardingState();
	const isInRandomEmojiCohort = autoPageEmojiCohort && autoPageEmojiCohort === 'random';

	useEffect(() => {
		onEmojiPickerLoaded && onEmojiPickerLoaded();
	}, [onEmojiPickerLoaded]);

	// One-way latch: once the AIFC emoji has landed (hasEmoji=true) we yield to standard
	// add/remove semantics for the rest of the session.
	const [aifcEmojiHasLandedOnce, setAifcEmojiHasLandedOnce] = useState(false);
	useEffect(() => {
		if (hasEmoji) {
			setAifcEmojiHasLandedOnce(true);
		}
	}, [hasEmoji]);

	const hasRemovedAIFCEmojiRef = useRef(false);
	const hasRemovedBlankPageAutoEmojiRef = useRef(false);
	const hasRemovedSuggestTitleEmojiRef = useRef(false);
	// Declared here (before the reset effect below) so the declare-before-use order
	// is preserved. The effect that fires it lives further down with the analytics.
	const hasFiredAifcSuggestionLandedRef = useRef(false);
	useEffect(() => {
		if (aiEmojiSuggestionSessionId !== undefined) {
			hasRemovedAIFCEmojiRef.current = false;
			hasRemovedBlankPageAutoEmojiRef.current = false;
			hasRemovedSuggestTitleEmojiRef.current = false;
			hasFiredAifcSuggestionLandedRef.current = false;
		}
	}, [aiEmojiSuggestionSessionId]);

	// useCallback so it can be added to the aifcSuggestionLanded effect deps below
	// without re-creating on every render (required by react-hooks/exhaustive-deps).
	const sendAnalyticsEvent = useCallback(
		({
			element,
			action,
			actionSubject,
			actionSubjectId,
			attributes = {},
		}: {
			element: string;
			action: string;
			actionSubject: string;
			actionSubjectId: string;
			attributes?: Record<string, string | boolean | null | undefined>;
		}) => {
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action,
						actionSubject,
						actionSubjectId,
						source: 'EmojiTitlePopover',
						attributes: {
							contentId,
							element,
							isLivePage,
							...attributes,
						},
					},
				}).fire();
			}
		},
		[createAnalyticsEvent, contentId, isLivePage],
	);

	// One-shot per popover lifecycle. Ref is declared higher up alongside the other
	// "has*Ref" latches so it can also be reset by the session-id effect.
	useEffect(() => {
		if (
			hasEmoji &&
			!hasFiredAifcSuggestionLandedRef.current &&
			isSmartCreate &&
			!!aiSuggestedEmojiId &&
			currentEmojiId === aiSuggestedEmojiId
		) {
			hasFiredAifcSuggestionLandedRef.current = true;
			sendAnalyticsEvent({
				element: 'pageEmoji',
				action: 'set',
				actionSubject: 'pageEmoji',
				actionSubjectId: 'aifcSuggestionLanded',
				attributes: {
					source: 'EmojiTitlePopover',
					cohort: autoPageEmojiCohort,
					contentType,
					aiSuggestedEmojiId,
					aiEmojiSuggestionSource,
				},
			});
		}
	}, [
		hasEmoji,
		isSmartCreate,
		aiSuggestedEmojiId,
		currentEmojiId,
		sendAnalyticsEvent,
		autoPageEmojiCohort,
		contentType,
		aiEmojiSuggestionSource,
	]);

	const isAifcLoadingRemoveDisable =
		!!aifcSuggestionInFlight && !hasEmoji && !aifcEmojiHasLandedOnce;
	const shouldShowRemoveVariant = hasEmoji || hasLiveDocsDefaultEmoji || isAifcLoadingRemoveDisable;

	const setEmojiTitleDiscoveredOnboardingState = () => {
		if (isInRandomEmojiCohort && !emojiTitleDiscovered) {
			setOnboardingState({
				key: NTH_USER_EMOJI_TITLE_DISCOVERED.EMOJI_TITLE_DISCOVERED,
				value: 'true',
			});

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'completed',
					actionSubject: 'emojiTitleDiscoveredOnboarding',
					source: 'EmojiTitlePopover',
					attributes: {
						cohort: 'random',
						contentType: contentType,
						isLivePage,
					},
				},
			}).fire();
		}
	};
	const handleFocus = useCallback(() => {
		setIsEmojiButtonFocused(true);
	}, [setIsEmojiButtonFocused]);

	const handleBlur = useCallback(() => {
		setIsEmojiButtonFocused(false);
	}, [setIsEmojiButtonFocused]);

	const openSecondEmojiPickerWithAnalytics = () => {
		// analytic event for clicking on button
		sendAnalyticsEvent({
			element: 'button',
			action: 'clicked',
			actionSubject: 'addEmojiButton',
			actionSubjectId: 'EmojiTitlePopover-addEmoji',
			attributes: {
				cohort: autoPageEmojiCohort,
				contentType: contentType,
			},
		});

		setOpenSecondEmojiPicker(!openSecondEmojiPicker);

		// analytic event for opening the emoji picker
		sendAnalyticsEvent({
			element: 'button',
			action: 'opened',
			actionSubject: 'emojiPicker',
			actionSubjectId: 'emojiPicker_opened',
			attributes: {
				triggerType: 'addEmojiButton, changeEmojiButton',
				inputMethod: 'title',
				cohort: autoPageEmojiCohort,
				contentType: contentType,
			},
		});
	};

	const restoreDefaultEmoji = () => {
		const hasRestorablePreviousEmoji =
			!!previousEmojiId &&
			previousEmojiId !== DefaultTitleEmojiContentPropertyValues.LIVE_PAGE_DEFAULT &&
			!!restorePreviousEmoji &&
			isExperimentEnabled('cc_page_experiences_livedocs_restore_latest_emoji');

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'restoreEmojiButton',
				actionSubjectId: 'restoreEmojiButton_clicked',
				source: 'EmojiTitlePopover',
				attributes: {
					contentId,
					isLivePage,
					cohort: autoPageEmojiCohort,
					contentType: contentType,
					restoredFromPreviousEmoji: hasRestorablePreviousEmoji,
					isRestoreLatestEmojiEnabled: isExperimentEnabled(
						'cc_page_experiences_livedocs_restore_latest_emoji',
					),
				},
			},
		}).fire();

		if (hasRestorablePreviousEmoji && restorePreviousEmoji) {
			void Promise.resolve(restorePreviousEmoji())
				.then(() => {
					emojiTitleState.justRestoredEmoji = true;
				})
				.catch(() => {
					emojiTitleState.justRestoredEmoji = false;
				});
			return;
		}

		void Promise.resolve(
			updateDefaultEmoji({ defaultTitleEmoji: DefaultTitleEmojiValues.LIVE_PAGE_DEFAULT }),
		)
			.then(() => {
				emojiTitleState.justRestoredEmoji = true;
			})
			.catch(() => {
				emojiTitleState.justRestoredEmoji = false;
			});
	};

	return (
		<Popup
			data-testid="emoji-popup"
			isOpen={openSecondEmojiPicker}
			autoFocus={false}
			popupComponent={PopupComponent as ComponentType<PopupComponentProps>}
			onClose={() => {}}
			content={() => emojiPicker}
			trigger={(triggerProps) => (
				<div
					data-testid="emoji-popup-button"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="emoji-popup-button"
					onFocus={handleFocus}
					onBlur={handleBlur}
				>
					{shouldShowRemoveVariant ? (
						// @ts-expect-error
						<Button
							ref={triggerProps.ref}
							aria-controls={triggerProps['aria-controls']}
							data-ds--level={triggerProps['data-ds--level']}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={floatingToolbarButtonStyle || {}}
							appearance="subtle"
							data-testid="emoji-remove-button"
							id="emoji-popup-button"
							isDisabled={isLoading || userOffline || isAifcLoadingRemoveDisable}
							spacing="compact"
							onClick={(event) => {
								onRemoveEmoji(event);

								setEmojiTitleDiscoveredOnboardingState();

								const removedAIFCSuggestedEmoji =
									!hasRemovedAIFCEmojiRef.current &&
									!!isSmartCreate &&
									!!aiSuggestedEmojiId &&
									currentEmojiId === aiSuggestedEmojiId;
								if (removedAIFCSuggestedEmoji) {
									hasRemovedAIFCEmojiRef.current = true;
								}

								const removedBlankPageAutoPasteSuggestedEmoji =
									!hasRemovedBlankPageAutoEmojiRef.current &&
									aiEmojiSuggestionSource === 'pasteAutoTitle' &&
									!!aiSuggestedEmojiId &&
									currentEmojiId === aiSuggestedEmojiId;
								if (removedBlankPageAutoPasteSuggestedEmoji) {
									hasRemovedBlankPageAutoEmojiRef.current = true;
								}

								const removedSuggestTitleSuggestedEmoji =
									!hasRemovedSuggestTitleEmojiRef.current &&
									aiEmojiSuggestionSource === 'suggestTitle' &&
									!!aiSuggestedEmojiId &&
									currentEmojiId === aiSuggestedEmojiId;
								if (removedSuggestTitleSuggestedEmoji) {
									hasRemovedSuggestTitleEmojiRef.current = true;
								}

								sendAnalyticsEvent({
									element: 'button',
									action: 'clicked',
									actionSubject: 'removeEmojiButton',
									actionSubjectId: 'EmojiTitlePopover-removeEmoji',
									attributes: {
										cohort: autoPageEmojiCohort,
										contentType: contentType,
										removedAIFCSuggestedEmoji,
										removedBlankPageAutoPasteSuggestedEmoji,
										removedSuggestTitleSuggestedEmoji,
										aiEmojiSuggestionSource,
										isRestoreLatestEmojiEnabled: isExperimentEnabled(
											'cc_page_experiences_livedocs_restore_latest_emoji',
										),
										removedRestoredEmoji: !!emojiTitleState.justRestoredEmoji,
									},
								});

								emojiTitleState.justRestoredEmoji = false;
							}}
							iconBefore={
								isLoading ? (
									<Spinner size="small" interactionName="emoji-title-popover" />
								) : (
									<span css={emojiIconStyles}>
										<CrossIcon label="" size="small" />
									</span>
								)
							}
						>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<span className="emoji-popup-button-text">
								{intl.formatMessage(i18n.removeEmoji)}
							</span>
						</Button>
					) : (
						// @ts-expect-error
						<Button
							ref={triggerProps.ref}
							aria-controls={triggerProps['aria-controls']}
							data-ds--level={triggerProps['data-ds--level']}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={floatingToolbarButtonStyle || {}}
							appearance="subtle"
							data-testid="emoji-add-button"
							id="emoji-popup-button"
							isDisabled={isLoading || userOffline}
							spacing="compact"
							isSelected={openSecondEmojiPicker}
							onClick={() => {
								isLivePage ? void restoreDefaultEmoji() : openSecondEmojiPickerWithAnalytics();
								setEmojiTitleDiscoveredOnboardingState();
							}}
							iconBefore={
								isLoading ? (
									<Spinner size="small" interactionName="emoji-title-popover" />
								) : (
									<span css={emojiIconStyles}>
										{isLivePage ? (
											<RefreshIcon label="" testId="restore-icon" />
										) : (
											<EmojiIcon label="" testId="add-emoji-icon" />
										)}
									</span>
								)
							}
						>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<span className="emoji-popup-button-text">
								{isLivePage
									? intl.formatMessage(i18n.restoreEmoji)
									: intl.formatMessage(i18n.emojiLabel)}
							</span>
						</Button>
					)}
				</div>
			)}
			placement="bottom-start"
			// match other title popups
			zIndex={700}
		/>
	);
};

type EmojiTitlePopoverPublicProps = Pick<
	EmojiTitleComponentPopoverProps,
	| 'contentType'
	| 'contentId'
	| 'onEmojiPickerLoaded'
	| 'floatingToolbarButtonStyle'
	| 'autoPageEmojiCohort'
	| 'emojiTitleDiscovered'
	| 'isSmartCreate'
	| 'onRemoveEmoji'
	| 'hasEmoji'
	| 'isLoading'
	| 'setOpenSecondEmojiPicker'
	| 'setIsEmojiButtonFocused'
	| 'openSecondEmojiPicker'
	| 'emojiPicker'
	| 'userOffline'
	| 'updateDefaultEmoji'
	| 'hasLiveDocsDefaultEmoji'
	| 'previousEmojiId'
	| 'restorePreviousEmoji'
	| 'currentEmojiId'
	| 'emojiTitleState'
>;

export const EmojiTitlePopover = withSessionData(
	EmojiTitlePopoverComponent,
) as FC<EmojiTitlePopoverPublicProps>;
EmojiTitlePopover.displayName = 'EmojiTitlePopover';
